import { humanizeConversionName } from 'src/utils/conversionLabels';

export const MAX_REPROCESS_ASSETS = 100;

const ASSET_ID_RE = /^[0-9a-f]{32}$/i;

export type ParsedIds = {
  /** Valid, de-duplicated, in the order pasted. */
  valid: string[];
  /** Tokens that are not a 32-character hex id. */
  invalid: string[];
  /** How many repeats were dropped. */
  duplicates: number;
};

/**
 * Accept ids however they arrive: commas, spaces, newlines, semicolons, or a
 * mix (a column pasted from a spreadsheet, a list from a ticket).
 */
export const parseAssetIds = (raw: string): ParsedIds => {
  const seen = new Set<string>();
  const valid: string[] = [];
  const invalid: string[] = [];
  let duplicates = 0;
  raw
    .split(/[\s,;]+/)
    .map((t) => t.trim())
    .filter(Boolean)
    .forEach((token) => {
      if (!ASSET_ID_RE.test(token)) {
        invalid.push(token);
        return;
      }
      const id = token.toLowerCase();
      if (seen.has(id)) {
        duplicates += 1;
        return;
      }
      seen.add(id);
      valid.push(id);
    });
  return { valid, invalid, duplicates };
};

/**
 * Only addons the backend reprocess path actually runs (#1017). Any other key
 * is silently dropped by `zoomnaut.reprocess_files` /
 * `ASSET_POST_PROCESS.SUPPORTED_ADDONS`, so offering it here only misleads.
 * REMOVE_BACKGROUND is supported there but deliberately left out: it is a
 * paid per-asset AI effect with its own bulk flow.
 */
export const REPROCESSABLE_ADDONS: { key: string; label: string; appliesTo: AssetKind }[] = [
  { key: 'ON_DEMAND_IMAGE', label: 'On-demand image', appliesTo: 'image' },
  { key: 'IMAGE_ANALYSIS', label: 'Image analysis (auto-tags)', appliesTo: 'image' },
  { key: 'FACE_RECOGNITION', label: 'Face recognition', appliesTo: 'image' },
  { key: 'PRINT_EXPORT', label: 'Print export', appliesTo: 'image' },
  { key: 'STORYBOARD', label: 'Video storyboard', appliesTo: 'video' },
];

export type AssetKind = 'image' | 'video';

export type TypeCounts = { image: number; video: number };

export type OutputGroupId = 'conversions' | 'transcodes' | 'addons';

export type OutputOption = { key: string; label: string; hint?: string };

export type OutputGroup = { id: OutputGroupId; title: string; options: OutputOption[] };

export const kindOf = (contentType?: string): AssetKind | 'other' => {
  const t = (contentType || '').toLowerCase();
  if (t.startsWith('image')) return 'image';
  if (t.startsWith('video')) return 'video';
  return 'other';
};

export const countKinds = (contentTypes: (string | undefined)[]): TypeCounts =>
  contentTypes.reduce<TypeCounts>(
    (acc, ct) => {
      const k = kindOf(ct);
      if (k !== 'other') acc[k] += 1;
      return acc;
    },
    { image: 0, video: 0 },
  );

/**
 * The outputs worth offering for a selection: image conversions when there is
 * an image or a video (video posters get conversions too), transcodes only
 * with videos, and addons by the type they act on. `counts` undefined means
 * "types not known yet" and offers everything.
 */
export const buildOutputGroups = (
  settings: any,
  counts?: TypeCounts,
): OutputGroup[] => {
  const hasImage = !counts || counts.image > 0;
  const hasVideo = !counts || counts.video > 0;
  const addons = settings?.addons ?? {};
  const groups: OutputGroup[] = [];

  const conversions: any[] = settings?.image?.conversions ?? [];
  if ((hasImage || hasVideo) && conversions.length) {
    groups.push({
      id: 'conversions',
      title: 'Image conversions',
      options: conversions.map((c) => ({ key: c.key, label: c.key })),
    });
  }

  const transcodes = Object.keys(settings?.video?.transcodes ?? {});
  if (hasVideo && transcodes.length) {
    groups.push({
      id: 'transcodes',
      title: 'Video transcodes',
      options: transcodes.map((key) => ({ key, label: humanizeConversionName(key), hint: key })),
    });
  }

  const addonOptions = REPROCESSABLE_ADDONS.filter(
    (a) =>
      addons[a.key]?.added &&
      addons[a.key]?.enabled &&
      (a.appliesTo === 'image' ? hasImage : hasVideo),
  ).map((a) => ({ key: a.key, label: a.label }));
  if (addonOptions.length) {
    groups.push({ id: 'addons', title: 'Addons', options: addonOptions });
  }

  return groups;
};

export type ReprocessSelection = Record<OutputGroupId, string[]>;

export const EMPTY_SELECTION: ReprocessSelection = { conversions: [], transcodes: [], addons: [] };

export const selectionCount = (s: ReprocessSelection) =>
  s.conversions.length + s.transcodes.length + s.addons.length;

/** Drop selected keys that the current groups no longer offer. */
export const pruneSelection = (s: ReprocessSelection, groups: OutputGroup[]): ReprocessSelection => {
  const offered = (id: OutputGroupId) =>
    new Set((groups.find((g) => g.id === id)?.options ?? []).map((o) => o.key));
  return {
    conversions: s.conversions.filter((k) => offered('conversions').has(k)),
    transcodes: s.transcodes.filter((k) => offered('transcodes').has(k)),
    addons: s.addons.filter((k) => offered('addons').has(k)),
  };
};
